import React, { ReactElement, memo, useEffect, useMemo } from "react";
import cx from "classnames";

// types
import { FoodItemTypeOfferBundles } from "./types/FoodItemTypeOfferBundles";
import { ExtraItemInlineWidgetProps } from "./interfaces";

// widgets
import { ExtraItemInfo } from "@cart-ui/widgets/food-item-info";

// helpers
import {
    getOfferBundleInfo,
    isExtraItemDataValid,
    isAutoAddOrOneTapType,
    isSwiggyOnePlanVariants,
} from "@cart-ui/helpers/OfferBundle";

// test ids
import { CART_ITEMS_TEST_IDS } from "@swiggy-private/cart-test-ids";

import styles from "./styles/FoodItem.scss";
import { useSessionStorage } from "@swiggy-private/checkout-core";
import { getNestedValue } from "@swiggy-private/js-utils";
import { ExtraItemTypes, PlanDetails } from "@swiggy-private/cart-types";
import { AUTO_ADD_OR_ONE_TAP_PLAN_TYPE_SESSION_KEY } from "@cart-ui/constants/OfferBundle";

// This is temporary code till we migarte to Cart 2.0. During migration we need to solve for this properly to accomodate Cart 2.0 icons
const CART_V1_LOGOS = {
    ONE: "v1634558776/swiggy_one/OneLogo_3x.png",
    ONE_LITE: "Swiggy%20One%20Lite/One_lite_Horizontal.png",
};

const ExtraItemInlineWidget = ({
    item,
    updating,
    onUpdateItem,
    analyticsEvents,
}: ExtraItemInlineWidgetProps): ReactElement | null => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [storedValue, setValue] = useSessionStorage(AUTO_ADD_OR_ONE_TAP_PLAN_TYPE_SESSION_KEY, "");

    const planDetails = useMemo(() => getNestedValue(item, "data.plan_details", {} as PlanDetails), [item]);

    const isOneProgram = useMemo(() => planDetails && isSwiggyOnePlanVariants(planDetails), [planDetails]);
    const onePlanType = useMemo(
        () => (planDetails && isOneProgram ? planDetails.type : ""),
        [isOneProgram, planDetails],
    );

    const extraItemInfo = useMemo(() => {
        if (isExtraItemDataValid(item) && isAutoAddOrOneTapType(item)) {
            return getOfferBundleInfo(item.data, { strikethrough: styles.strikethrough });
        }

        return null;
    }, [item]);

    const stripClasses = useMemo(
        () => ({
            [styles.stripContainer]: isAutoAddOrOneTapType(item),
            [styles.autoAddColorPeach]:
                isAutoAddOrOneTapType(item) && isSwiggyOnePlanVariants(item?.data?.plan_details),
            [styles.offerPackColor]: isAutoAddOrOneTapType(item) && !isSwiggyOnePlanVariants(item?.data?.plan_details),
        }),
        [item],
    );

    const extraItemLogo = useMemo(() => {
        if (item.type === ExtraItemTypes.NUDGE_TYPE_AUTO_ADD && isOneProgram) {
            return onePlanType === "PLAN_TYPE_LITE" ? CART_V1_LOGOS.ONE_LITE : CART_V1_LOGOS.ONE;
        }
        return item?.data?.logo_ctx?.logo;
    }, [isOneProgram, item?.data?.logo_ctx?.logo, item.type, onePlanType]);

    useEffect(() => {
        if (isAutoAddOrOneTapType(item) && isOneProgram) {
            setValue(onePlanType);
        } else {
            setValue("");
        }
    }, [isOneProgram, item, onePlanType, setValue]);

    if (!item) return null;

    return (
        <div className={cx(styles.container, stripClasses)} data-testid={CART_ITEMS_TEST_IDS.CART_ITEM_EXTRA_ITEM}>
            <FoodItemTypeOfferBundles
                onUpdateItem={onUpdateItem}
                updating={updating}
                item={item?.data}
                analyticsEvents={analyticsEvents}
                onePlanType={onePlanType}
            >
                <div className={cx(styles.infoWrapper)}>
                    <ExtraItemInfo
                        name={extraItemInfo?.name}
                        subText={extraItemInfo?.subText}
                        planDetails={item?.data?.plan_details}
                        logo={extraItemLogo}
                    />
                </div>
            </FoodItemTypeOfferBundles>
        </div>
    );
};

export default memo(ExtraItemInlineWidget);
